import { useEffect } from 'preact/hooks';
import { FEATURES, FILM_ROWS, HELP, TASKS } from '../data/guide';
import { FILMS } from '../data/whatsNew';
import { isIOSDevice, isStandalone } from '../live/install';
import { calendarFilmOpen, introOpen, panel } from '../state';
import { Sheet } from './chrome';
import { helpSeen, showAround, showMe } from './Guide';
import { Icon } from './icons';
import { WhatsNewRow } from './WhatsNew';

// Help: the way to tell us something is not right, a tour of the buttons, then what each update brought, how to do everyday things, the films and
// everything Ahillah can do. Opened from the question mark at the top, from Settings and from What's new. Every
// word is in src/data/guide.ts.

export function HelpPanel() {
  // Someone who has found Help has found the tour: a new user is not offered it again on the sky.
  useEffect(helpSeen, []);
  const films = FILM_ROWS.filter((f) => FILMS[f.film].offered);
  const watch = (f: 'intro' | 'calendar') => {
    panel.value = null;
    if (f === 'intro') introOpen.value = true;
    else calendarFilmOpen.value = true;
  };
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label={HELP.title}>
      <h2 class="sheet-title">{HELP.title}</h2>
      {/* First: the way to tell us something is not right (src/ui/FeedbackPanel.tsx). */}
      <div class="results">
        <button class="result" onClick={() => (panel.value = 'feedback')}>
          <span class="result-icon"><Icon name="message" size={20} /></span>
          <span class="result-text">
            <span class="result-label">{HELP.feedback}</span>
            <span class="result-sub">{HELP.feedbackSub}</span>
          </span>
          <Icon name="chevron" size={16} />
        </button>
      </div>
      <div class="card wide help-tour">
        <div class="card-title"><Icon name="compass" size={16} /> {HELP.tourTitle}</div>
        <div class="card-sub">{HELP.tourText}</div>
        <div class="tonight-actions">
          <button class="btn" onClick={() => showAround()}>{HELP.tourStart}</button>
        </div>
      </div>
      <div class="results"><WhatsNewRow /></div>

      <h3 class="section-title">{HELP.howTitle}</h3>
      <div class="results howto-list">
        {TASKS.filter((t) => !t.ios || (isIOSDevice() && isStandalone())).map((t) => (
          <details class="howto" key={t.id}>
            <summary class="result">
              <span class="result-icon"><Icon name={t.icon} size={20} /></span>
              <span class="result-text"><span class="result-label">{t.title}</span></span>
              <Icon name="chevron" size={16} />
            </summary>
            <div class="howto-body">
              <p>{t.answer}</p>
              {t.steps && <ol class="howto-steps">{t.steps.map((s) => <li key={s}>{s}</li>)}</ol>}
              <button class="btn-ghost small" onClick={() => showMe(t)} aria-label={`${HELP.showMe}: ${t.title}`}>
                {HELP.showMe} <Icon name="chevron" size={14} />
              </button>
            </div>
          </details>
        ))}
      </div>

      {/* Only the films on offer (src/data/whatsNew.ts); none, and the section goes. */}
      {films.length > 0 && (
        <>
          <h3 class="section-title">{HELP.filmsTitle}</h3>
          <div class="results">
            {films.map((f) => (
              <button class="result" key={f.film} onClick={() => watch(f.film)}>
                <span class="result-icon"><Icon name="play" size={20} /></span>
                <span class="result-text">
                  <span class="result-label">{f.title}</span>
                  <span class="result-sub">{f.sub}</span>
                </span>
                <Icon name="chevron" size={16} />
              </button>
            ))}
          </div>
        </>
      )}

      <h3 class="section-title">{HELP.featuresTitle}</h3>
      <div class="results">
        {FEATURES.map(([icon, title, text, target]) => {
          const inner = (
            <>
              <span class="result-icon"><Icon name={icon} size={20} /></span>
              <span class="result-text">
                <span class="result-label">{title}</span>
                <span class="result-sub">{text}</span>
              </span>
            </>
          );
          return target ? (
            <button class="result" key={title} onClick={() => (panel.value = target)}>{inner}<Icon name="chevron" size={16} /></button>
          ) : (
            <div class="result" key={title}>{inner}</div>
          );
        })}
      </div>
      <p class="fine">{HELP.fine}</p>
    </Sheet>
  );
}
